import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { contentStyles, paragraphRegular } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
const Form = styled.form `
  --bgColour: ${brand.primary.white};
  --borderColour: ${brand.primary.black};
  --infoBgColour: ${brand.grey.g200};
  --placeholderColour: ${brand.grey.g500};
  --readOnlyColour: ${brand.grey.g400};
  --textColour: ${brand.primary.black};

  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${fluid(32, 40)} 0;
`;
const Section = styled.fieldset `
  display: flex;
  flex-direction: column;
  gap: ${fluid(20, 24)};

  &[data-visible='false'] {
    display: none;
  }
`;
const Title = styled.legend `
  ${fonts.poppins.bold};

  display: block;
  font-size: ${fluid(18, 20)};
  margin: 0 0 30px;
  padding: 0 0 8px;
  color: var(--textColour);
  border-bottom: 1px solid var(--borderColour);
  width: 100%;

  @media ${from(Device.Tablet)} {
    margin-bottom: ${fluid(20, 24)};
  }
`;
const Content = styled.p `
  ${paragraphRegular}
`;
const Row = styled.div `
  --count: 1;

  display: grid;
  grid-template-columns: repeat(var(--count), 1fr);
  gap: ${fluid(20, 24)};

  @media ${from(Device.Tablet)} {
    --count: 2;

    &[data-layout='full'] {
      --count: 1;
    }
  }
`;
export const FormFieldStyles = css `
  --inputHeight: 50px;

  display: flex;
  flex-direction: column;
  position: relative;
`;
const Field = styled.div `
  ${FormFieldStyles};

  @media ${from(Device.Tablet)} {
    &[data-size='wide'] {
      grid-column: span 2;
    }

    &[data-size='full'] {
      grid-column: span 2;
    }
  }

  @media ${from(Device.Desktop)} {
    &[data-size='full'] {
      grid-column: span 4;
    }
  }
`;
export const TextareaLabelStyles = css `
  --translateY: calc(${fluid(12, 14)} * -1);

  -webkit-line-clamp: 4;
`;
export const CommonLabelStyles = css `
  ${transition('font-size, font-weight, transform')}
  ${fonts.poppins.regular};

  --translateY: -50%;

  position: absolute;
  top: calc(var(--inputHeight) / 2);
  left: ${fluid(10, 18)};
  padding: 2px 8px;
  font-size: ${fluid(15, 16)};
  line-height: 1.35;
  display: block;
  margin: 0 0 ${fluid(8, 14)};
  color: var(--textColour);
  background-color: var(--bgColour);
  transform: translateY(var(--translateY));
  pointer-events: none;
  z-index: 1;
  max-width: calc(100% - (${fluid(10, 18)} * 2));
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;

  ${Field}:has(textarea) & {
    ${TextareaLabelStyles};
  }

  &.required,
  &[data-required='true'] {
    &::after {
      content: ' *';
      display: inline;
    }
  }

  &[data-hide='true'] {
    ${srOnly}
  }

  [data-error='true'] & {
    --textColour: ${brand.primary.orangeDark};
  }
`;
export const ActiveLabelStyles = css `
  font-size: ${fluid(13, 18)};
  font-weight: 800;
  transform: translateY(calc((${fluid(12, 14)} * -1) - var(--inputHeight) / 2));
  -webkit-line-clamp: 1;
`;
const Label = styled.label `
  ${CommonLabelStyles};

  /* When input has content or is focused, move the label to overlay the top border */
  &:has(+ input:focus, + textarea:focus),
  &:has(+ input:not(:placeholder-shown), + textarea:not(:placeholder-shown)),
  &[data-move-label='true'] {
    ${ActiveLabelStyles};
  }
`;
const Description = styled.div `
  ${paragraphRegular};
`;
export const CommonErrorStyles = css `
  ${fonts.poppins.semiBold};

  display: block;
  color: ${brand.primary.orangeDark};
  margin: 8px 0 0;
`;
const Error = styled.span `
  ${CommonErrorStyles};
`;
const Divider = styled.hr `
  display: block;
  border: 0;
  border-bottom: 1px solid var(--borderColour);
  margin: 0;
  padding: 0;
`;
const SubmitWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;

  ${ButtonStyles.Container} {
    width: auto;
  }

  @media ${from(Device.Tablet)} {
    flex-direction: row;
    align-items: center;
    gap: ${fluid(20, 24)};

    ${ButtonStyles.Container} {
      margin-left: auto;
    }
  }
`;
const Loader = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  background: ${rgba(brand.primary.black, 0.2)};
  color: ${brand.primary.white};
  flex-direction: column;
  height: 100%;
  place-content: center;
  place-items: center;
  text-align: center;
  width: 100%;
`;
const LoaderText = styled.span `
  ${paragraphRegular};
  ${fonts.poppins.bold};

  display: block;
  margin: 1rem 0 0;
`;
const ErrorList = styled.ul `
  display: flex;
  color: ${brand.primary.orangeDark};
  flex-direction: column;
  margin: 0;
`;
const ErrorListItem = styled.li `
  ${CommonErrorStyles};

  margin: 0;

  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;
const ConfirmationMessage = styled.div `
  ${contentStyles};
`;
const FormStyles = {
    ConfirmationMessage,
    Content,
    Description,
    Divider,
    Error,
    ErrorList,
    ErrorListItem,
    Field,
    Form,
    Label,
    Loader,
    LoaderText,
    Row,
    Section,
    SubmitWrapper,
    Title,
};
export default FormStyles;
